import { metaOnboardingApi } from "../api/MetaOnboardingApi";
import {
  MetaOnboardingConfigResponse,
  MetaOnboardingStatusResponse,
  MetaWhatsappBusinessProfile,
  MetaWhatsappBusinessProfileRequest,
  MetaWhatsappTemplateCreateRequest,
  MetaWhatsappTemplate,
  MetaWhatsappCompleteRequest,
  MetaWhatsappRegisterPhoneRequest,
  MetaWhatsappTestMessageRequest,
} from "../models/Meta/MetaOnboardingModels";

const META_REQUEST_TIMEOUT_MS = 20000;
const META_PROFILE_REQUEST_TIMEOUT_MS = 90000;

const withTimeout = async <T,>(
  promise: Promise<T>,
  message: string,
  timeoutMs = META_REQUEST_TIMEOUT_MS,
): Promise<T> => {
  let timeoutId: ReturnType<typeof setTimeout>;
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error(message)), timeoutMs);
  });
  try {
    return await Promise.race([promise, timeout]);
  } finally {
    clearTimeout(timeoutId!);
  }
};

class MetaOnboardingManager {
  private static instance: MetaOnboardingManager;

  public static get(): MetaOnboardingManager {
    if (!this.instance) {
      this.instance = new MetaOnboardingManager();
    }
    return this.instance;
  }

  async getConfig(): Promise<MetaOnboardingConfigResponse | undefined> {
    const result = await withTimeout(
      metaOnboardingApi.config.requestHard(null),
      "No se pudo cargar la configuración de Meta. Revisa la API o intenta de nuevo."
    );
    if (!result.isSuccess) throw new Error(result.messageError);
    return result.data;
  }

  async getStatus(): Promise<MetaOnboardingStatusResponse | undefined> {
    const result = await withTimeout(
      metaOnboardingApi.status.requestHard(null),
      "No se pudo consultar el estado de WhatsApp. Revisa la API o intenta de nuevo."
    );
    if (!result.isSuccess) throw new Error(result.messageError);
    return result.data;
  }

  async completeWhatsapp(
    payload: MetaWhatsappCompleteRequest
  ): Promise<MetaOnboardingStatusResponse | undefined> {
    const result = await withTimeout(
      metaOnboardingApi.completeWhatsapp.requestHard(payload),
      "Meta autorizo el flujo, pero Lypro no pudo completar la conexión a tiempo."
    );
    if (!result.isSuccess) throw new Error(result.messageError);
    return result.data;
  }

  async resetWhatsapp(): Promise<MetaOnboardingStatusResponse | undefined> {
    const result = await withTimeout(
      metaOnboardingApi.resetWhatsapp.requestHard(null),
      "No se pudo reiniciar la conexión de WhatsApp a tiempo."
    );
    if (!result.isSuccess) throw new Error(result.messageError);
    return result.data;
  }

  async sendTestMessage(
    payload: MetaWhatsappTestMessageRequest
  ): Promise<boolean> {
    const result = await withTimeout(
      metaOnboardingApi.testMessage.requestHard(payload),
      "No se pudo enviar el mensaje de prueba a tiempo."
    );
    if (!result.isSuccess) throw new Error(result.messageError);
    return result.data?.sent === true;
  }

  async validateCloudApi(): Promise<MetaOnboardingStatusResponse | undefined> {
    const result = await withTimeout(
      metaOnboardingApi.validateCloudApi.requestHard(null),
      "No se pudo validar WhatsApp Cloud API a tiempo."
    );
    if (!result.isSuccess) throw new Error(result.messageError);
    return result.data;
  }

  async registerPhone(
    payload: MetaWhatsappRegisterPhoneRequest
  ): Promise<MetaOnboardingStatusResponse | undefined> {
    const result = await withTimeout(
      metaOnboardingApi.registerPhone.requestHard(payload),
      "No se pudo registrar el número en WhatsApp Cloud API a tiempo."
    );
    if (!result.isSuccess) throw new Error(result.messageError);
    return result.data;
  }

  async getBusinessProfile(): Promise<MetaWhatsappBusinessProfile | undefined> {
    const result = await withTimeout(
      metaOnboardingApi.businessProfile.requestHard(null),
      "No se pudo consultar el perfil de WhatsApp a tiempo."
    );
    if (!result.isSuccess) throw new Error(result.messageError);
    return result.data;
  }

  async updateBusinessProfile(
    payload: MetaWhatsappBusinessProfileRequest
  ): Promise<MetaOnboardingStatusResponse | undefined> {
    const result = await withTimeout(
      metaOnboardingApi.updateBusinessProfile.requestHard(payload),
      "No se pudo actualizar el perfil de WhatsApp a tiempo.",
      META_PROFILE_REQUEST_TIMEOUT_MS,
    );
    if (!result.isSuccess) throw new Error(result.messageError);
    return result.data;
  }

  async getGreetingTemplate(): Promise<MetaWhatsappTemplate | null | undefined> {
    const result = await withTimeout(
      metaOnboardingApi.greetingTemplate.requestHard(null),
      "No se pudo consultar la plantilla de saludo a tiempo."
    );
    if (!result.isSuccess) throw new Error(result.messageError);
    return result.data;
  }

  async createGreetingTemplate(): Promise<MetaWhatsappTemplate | undefined> {
    const result = await withTimeout(
      metaOnboardingApi.createGreetingTemplate.requestHard(null),
      "No se pudo crear la plantilla de saludo a tiempo."
    );
    if (!result.isSuccess) throw new Error(result.messageError);
    return result.data;
  }

  async listTemplates(): Promise<MetaWhatsappTemplate[] | undefined> {
    const result = await withTimeout(
      metaOnboardingApi.listTemplates.requestHard(null),
      "No se pudieron consultar las plantillas a tiempo."
    );
    if (!result.isSuccess) throw new Error(result.messageError);
    return result.data || [];
  }

  async createTemplate(
    payload: MetaWhatsappTemplateCreateRequest
  ): Promise<MetaWhatsappTemplate | undefined> {
    const result = await withTimeout(
      metaOnboardingApi.createTemplate.requestHard(payload),
      "No se pudo crear la plantilla a tiempo."
    );
    if (!result.isSuccess) throw new Error(result.messageError);
    return result.data;
  }

  async sendTemplate(
    payload: MetaWhatsappTestMessageRequest
  ): Promise<boolean> {
    const result = await withTimeout(
      metaOnboardingApi.sendTemplate.requestHard(payload),
      "No se pudo enviar la plantilla a tiempo."
    );
    if (!result.isSuccess) throw new Error(result.messageError);
    return result.data?.sent === true;
  }
}

export const metaOnboardingManager = MetaOnboardingManager.get();
